/**
 * Динамик тохиргоо (`pf_config_rule`) — FRONTEND дээрх ҮНЭЛГЭЭ ба санах ойн кэш.
 *
 * ⚠ Энэ файл нь ЦЭВЭР: сүлжээ, sessionStorage хөндөхгүй. Татах/хадгалах нь
 * `configRulesLoader.js`-д. Ингэснээр `exprEngine.js` нь HTTP клиент рүү
 * гинжин хамаарал үүсгэхгүй (jest дээр axios ESM-ээс болж унадаг байсан).
 *
 * ⚠ ЯАГААД: өмнө нь дүрмийг талбар тутамд сервер рүү асуудаг байсан нь удаан.
 * Одоо бүх дүрмийг НЭГ удаа татаж (`pf002806`) кэшлээд, үнэлгээг эндээ хийнэ.
 * Сервер рүү дахин очихгүй.
 *
 * ⚠ ХАМГИЙН ЧУХАЛ: доорх нөхцөл үнэлэх ба дүрэм сонгох логик нь backend-ийн
 * `app/Helpers/helpers.php` (`configConditionPasses`, `configResolveRule`)-тэй
 * ЯГ ИЖИЛ байх ёстой. Аль нэгийг өөрчилбөл нөгөөг нь ЗААВАЛ дагалдуулна —
 * эс бөгөөс нэг тохиргоо frontend дээр нэг, сервер дээр өөр хариу өгнө.
 *
 * ⚠ Сервер нь ЭРХ МЭДЭЛТЭЙ тал хэвээр. Энд тооцсон утга нь хэрэглэгчид
 * харуулах зорилготой; хязгаар/хориг шалгалтыг `tosteps` алдааны нөхцөлд
 * (`isWithinConfigRange`) сервер талд давхар хийнэ.
 */



/** { version, rules: [...] } — хүчин төгөлдөр кэш. */
let cache = null;
/** Зэрэг дуудлагыг нэгтгэх promise (олон талбар зэрэг асуувал 1 л хүсэлт). */
let inflight = null;
/** `instid`, `branch` — backend `configBuildContext`-ийн автомат нэмдэг утгууд. */
let contextDefaults = {};

// ---------------------------------------------------------------------------
// Утга хэвшүүлэх — backend `configUnwrapScalar` / `configNormalizeScalar`-ийн хуулбар
// ---------------------------------------------------------------------------

/**
 * select (3) / lookup (6) талбарын ОБЪЕКТ утгаас `value`-г гаргана.
 *
 * ⚠ Зөвхөн `value` түлхүүрийг хардаг — `exprEngine`-ийн `fval` нь `id`/`code`/`key`
 * рүү ч уначихдаг, гэхдээ backend тэгдэггүй тул ЭНД давтахгүй.
 */
export const unwrapScalar = (value) => {
    if (value !== null && typeof value === "object") {
        const inner = Array.isArray(value) ? undefined : value.value;
        if (inner === undefined || inner === null) return null;
        return typeof inner === "object" ? null : inner;
    }
    return value;
};

/** PHP-ийн `is_numeric`-тэй ижил: хоосон мөр, boolean нь ТОО БИШ. */
export const isNumeric = (v) => {
    if (typeof v === "number") return Number.isFinite(v);
    if (typeof v !== "string") return false;
    const t = v.trim();
    return t !== "" && Number.isFinite(Number(t));
};

/** Харьцуулалтад: тоо бол number, бусад бол trim+lowercase мөр. */
export const normalizeScalar = (value) => {
    const v = unwrapScalar(value);
    if (v === null || v === undefined) return null;
    if (typeof v === "boolean") return v ? 1 : 0;
    if (isNumeric(v)) return Number(typeof v === "string" ? v.trim() : v);
    return String(v).trim().toLowerCase();
};

/** PHP `(string)` — null → "", тоо → "5" / "2.5". */
const toStr = (v) => (v === null || v === undefined ? "" : String(v));

export const scalarEquals = (a, b) => {
    const na = normalizeScalar(a);
    const nb = normalizeScalar(b);
    if (typeof na === "number" && typeof nb === "number") {
        return Math.abs(na - nb) < 0.00001;
    }
    return toStr(na) === toStr(nb);
};

// ---------------------------------------------------------------------------
// Нөхцөл — backend `configConditionPasses`-ийн хуулбар
// ---------------------------------------------------------------------------

/**
 * ⚠ Context-д БАЙХГҮЙ талбарыг ХАНГАГДААГҮЙ гэж тооцно (backend-тэй ижил).
 * ⚠ `BETWEEN` нь ХОЁР ТАЛ ХААЛТТАЙ: 600–699 нь 699-ийг агуулна.
 */
export const conditionPasses = (cond, context) => {
    if (!cond || typeof cond !== "object" || Array.isArray(cond)) return false;

    const field = cond.name;
    const op = String(cond.cond ?? "").trim().toUpperCase();
    let values = cond.values;
    if (!Array.isArray(values)) values = values === undefined ? [] : [values];

    if (!field || op === "") return false;
    if (!Object.prototype.hasOwnProperty.call(context ?? {}, field)) return false;

    const actual = unwrapScalar(context[field]);

    switch (op) {
        case "=":
        case "==":
            return values.length > 0 && scalarEquals(actual, values[0]);

        case "!=":
        case "<>":
            return values.length > 0 && !scalarEquals(actual, values[0]);

        case "IN":
            return values.some((c) => scalarEquals(actual, c));

        case "NOT IN":
        case "NOTIN":
            return !values.some((c) => scalarEquals(actual, c));

        case ">":
        case ">=":
        case "<":
        case "<=": {
            if (!isNumeric(actual) || values.length === 0 || !isNumeric(values[0])) return false;
            const a = Number(actual);
            const b = Number(values[0]);
            if (op === ">") return a > b;
            if (op === ">=") return a >= b;
            if (op === "<") return a < b;
            return a <= b;
        }

        case "BETWEEN": {
            if (!isNumeric(actual) || values.length < 2) return false;
            if (!isNumeric(values[0]) || !isNumeric(values[1])) return false;
            const a = Number(actual);
            return a >= Number(values[0]) && a <= Number(values[1]);
        }

        default:
            return false;
    }
};

// ---------------------------------------------------------------------------
// Дүрэм сонгох — backend `configResolveRule`-ийн хуулбар
// ---------------------------------------------------------------------------

/**
 * Нөхцөл хангасан ХАМГИЙН тохирох дүрэм.
 *
 * Эрэмбэ: priority буурахаар → урсгалд хавсаргасан нь ерөнхийгөөс дээгүүр →
 * нөхцөлийн тоо их нь → id буурахаар. Зөвхөн ЯЛСАН дүрмийн параметр
 * хэрэгжинэ — нэгтгэл БАЙХГҮЙ.
 */
export const resolveConfigRule = (code, context = {}, flowid = null) => {
    if (!cache) return null;

    const ctx = { ...contextDefaults, ...context };
    const matched = [];

    for (const rule of cache.rules) {
        if (rule.code !== code) continue;

        // flowid өгөөгүй → ЗӨВХӨН бүх урсгалд хамаарах дүрмүүд.
        if (flowid === null || flowid === undefined) {
            if (rule.flowid !== null && rule.flowid !== undefined) continue;
        } else if (
            rule.flowid !== null &&
            rule.flowid !== undefined &&
            Number(rule.flowid) !== Number(flowid)
        ) {
            continue;
        }

        const conditions = Array.isArray(rule.conditions) ? rule.conditions : [];
        if (conditions.every((c) => conditionPasses(c, ctx))) {
            matched.push({ rule, weight: conditions.length });
        }
    }

    if (matched.length === 0) return null;

    matched.sort((x, y) => {
        const p = Number(y.rule.priority ?? 0) - Number(x.rule.priority ?? 0);
        if (p !== 0) return p;
        const f =
            (y.rule.flowid !== null && y.rule.flowid !== undefined ? 1 : 0) -
            (x.rule.flowid !== null && x.rule.flowid !== undefined ? 1 : 0);
        if (f !== 0) return f;
        const w = y.weight - x.weight;
        if (w !== 0) return w;
        return Number(y.rule.id) - Number(x.rule.id);
    });

    return matched[0].rule;
};

// ---------------------------------------------------------------------------
// Нийтийн API — backend helper-үүдтэй ижил нэр, ижил гарц
// ---------------------------------------------------------------------------

/** Ялсан дүрмийн бүх параметр, нэрээр индекслэгдсэн. Олдоогүй бол `{}`. */
export const getConfigParams = (code, context = {}, flowid = null) => {
    const rule = resolveConfigRule(code, context, flowid);
    return rule ? rule.params ?? {} : {};
};

/** Нэг параметрийн бичлэг (`valuetype`, `min`, `max`, `value`, `name`) эсвэл null. */
export const getConfigParam = (code, paramname, context = {}, flowid = null) =>
    getConfigParams(code, context, flowid)[paramname] ?? null;

/**
 * Нэг параметрийн УТГА. Хамгийн түгээмэл хэрэглээ.
 *
 * ⚠ Нэр нь зориуд `getConfigValue` БИШ: backend дээр тэр нэртэй helper нь
 * ХУУЧИН `pf_flow_range`-ийг уншдаг (огт өөр эх сурвалж). Андуурахгүйн тулд.
 */
export const getConfigParamValue = (code, paramname, context = {}, flowid = null) =>
    getConfigParam(code, paramname, context, flowid)?.value ?? null;

/**
 * `range` төрлийн параметрийн ДООД / ДЭЭД хязгаар.
 *
 * ⚠ ЯАГААД ТУСДАА: `range` параметрийн `value` нь ХООСОН (хил нь min/max-д)
 * тул `getConfigParamValue` нь null буцаана. Хугацаа/дүнгийн хязгаарыг
 * нөхцөлд шууд ашиглахад эдгээр хэрэгтэй.
 */
export const getConfigMin = (code, paramname, context = {}, flowid = null) =>
    getConfigParam(code, paramname, context, flowid)?.min ?? null;

export const getConfigMax = (code, paramname, context = {}, flowid = null) =>
    getConfigParam(code, paramname, context, flowid)?.max ?? null;

/**
 * Утга тохиргооны мин–макс хүрээнд байна уу. ҮНЭН = зөв.
 *
 * ⚠ Дүрэм/параметр ОЛДООГҮЙ бол `whenMissing` (анхдагч true) — тохируулаагүй
 * бүтээгдэхүүнийг хаахгүй. Backend-тэй ижил.
 */
export const isWithinConfigRange = (
    code,
    paramname,
    value,
    context = {},
    flowid = null,
    whenMissing = true
) => {
    const param = getConfigParam(code, paramname, context, flowid);
    if (param === null) return !!whenMissing;

    const v = unwrapScalar(value);
    if (!isNumeric(v)) return false;

    const n = Number(v);
    if (param.min !== null && param.min !== undefined && n < param.min) return false;
    if (param.max !== null && param.max !== undefined && n > param.max) return false;
    return true;
};

// ---------------------------------------------------------------------------
// Кэшийн агуулах (IO-гүй — татах логик нь `configRulesLoader.js`-д)
// ---------------------------------------------------------------------------

/**
 * Автоматаар нэмэгдэх context (`instid`, `branch`).
 *
 * ⚠ Эдгээрийг frontend дээр ГАРГАЖ АВАХГҮЙ — серверийн `configBuildContext`
 * тооцоод bundle-тай хамт буцаадаг. Тэгснээр нөхцөлийн үнэлгээ хоёр тал
 * дээр яг ижил болно.
 */
export const setConfigContextDefaults = (defaults = {}) => {
    contextDefaults = { ...defaults };
};

/** Татаж авсан bundle-ийг санах ойн кэшэд суулгана. */
export const setConfigRules = (payload) => {
    if (!payload) {
        cache = null;
        contextDefaults = {};
        return null;
    }
    cache = {
        version: payload.version ?? null,
        rules: Array.isArray(payload.rules) ? payload.rules : [],
        context: payload.context ?? {},
    };
    contextDefaults = { ...cache.context };
    return cache;
};

export const getConfigCache = () => cache;

export const getConfigVersion = () => cache?.version ?? null;

export const isConfigLoaded = () => !!cache;

/** Тестэд зориулсан — кэшийг шууд суулгана (сүлжээгүйгээр). */
export const __setConfigCacheForTest = (payload) =>
    setConfigRules(payload ? { version: payload.version ?? "test", ...payload } : null);

export default {
    setConfigRules,
    setConfigContextDefaults,
    getConfigParams,
    getConfigParam,
    getConfigParamValue,
    getConfigMin,
    getConfigMax,
    isWithinConfigRange,
    resolveConfigRule,
    getConfigCache,
    getConfigVersion,
    isConfigLoaded,
};
